import type { AnnotationStatus, GearAbility, GearItem } from "@shared/entities";
import { ANNOTATION_STATUSES, GEAR_ITEM_MAX_LEVEL } from "@shared/entities";
import type { GearRarity, GearSlotId } from "@shared/game";
import { EFFECT_TYPE_SUGGESTIONS, GEAR_RARITIES, GEAR_SLOTS, RARITY_LABEL, SLOT_LABEL } from "@shared/game";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useState } from "react";
import toast from "react-hot-toast";
import { RarityText, StatusDot } from "../components/chips";
import { LoadError, Loading, PageHeader } from "../components/page-state";
import { errorMessage } from "../lib/api";
import { formatShort } from "../lib/format";
import { queryClient } from "../lib/query-client";
import { createGearItems, deleteGearItem, gearItemsQuery, updateGearItem } from "../lib/queries";

export const Route = createFileRoute("/gear/catalog")({
	component: CatalogPage,
	validateSearch: (s: Record<string, unknown>): { slot?: GearSlotId } =>
		GEAR_SLOTS.includes(s["slot"] as GearSlotId) ? { slot: s["slot"] as GearSlotId } : {},
});

const STATUS_LABEL: Record<AnnotationStatus, string> = { draft: "Draft", in_review: "In review", final: "Final" };
const muted = { color: "var(--color-ink-400)" };
const subtle = { color: "var(--color-ink-300)" };

/**
 * The catalog is a workbook: one sheet per slot, six rarity bands per sheet,
 * and every cell is typed into directly. Nothing here comes from the game;
 * it is the designers' draft of items the game does not have yet.
 */
function CatalogPage() {
	const items = useQuery(gearItemsQuery);
	const { slot = "helmet" } = Route.useSearch();
	const navigate = useNavigate();
	const [saveState, setSaveState] = useState<{ kind: "idle" } | { kind: "saving" } | { kind: "saved"; at: string }>({ kind: "idle" });

	const onSaving = useCallback(() => setSaveState({ kind: "saving" }), []);
	const onSaved = useCallback(() => setSaveState({ kind: "saved", at: new Date().toISOString() }), []);

	if (items.isPending) return <Loading what="Loading the catalog" />;
	if (items.isError) return <LoadError error={items.error} retry={() => void items.refetch()} />;

	const mine = items.data.filter((i) => i.slot === slot);
	const countBySlot = new Map(GEAR_SLOTS.map((s) => [s, items.data.filter((i) => i.slot === s).length]));

	return (
		<div className="space-y-6">
			<PageHeader
				title="Gear catalog"
				eyebrow={`${items.data.length} items drafted. The game has none of these yet; this sheet is the proposal.`}
			>
				<span className="text-xs" style={muted} aria-live="polite">
					{saveState.kind === "saving" && "Saving…"}
					{saveState.kind === "saved" && `Saved ${formatShort(saveState.at)}`}
				</span>
				<a className="btn" href="/api/gear-items/export" download="gear_items.json">
					Download as JSON
				</a>
			</PageHeader>

			<div role="tablist" aria-label="Slot" className="flex flex-wrap gap-1 border-b" style={{ borderColor: "var(--color-ink-700)" }}>
				{GEAR_SLOTS.map((s) => {
					const active = s === slot;
					return (
						<button
							key={s}
							type="button"
							role="tab"
							aria-selected={active}
							className="-mb-px flex items-baseline gap-2 rounded-t-md border px-3.5 py-2 text-sm transition-colors"
							style={{
								borderColor: active ? "var(--color-ink-700)" : "transparent",
								borderBottomColor: active ? "var(--color-ink-900)" : "transparent",
								background: active ? "var(--color-ink-900)" : "transparent",
								color: active ? "var(--color-gold-400)" : "var(--color-ink-300)",
							}}
							onClick={() => void navigate({ to: "/gear/catalog", search: s === "helmet" ? {} : { slot: s } })}
						>
							{SLOT_LABEL[s]}
							<span className="num text-xs" style={muted}>
								{countBySlot.get(s)}
							</span>
						</button>
					);
				})}
			</div>

			<div className="panel overflow-x-auto">
				<table className="sheet">
					<thead>
						<tr>
							<th className="w-[15rem]">Name</th>
							<th className="num w-[5.5rem]">ATK</th>
							<th className="num w-[5.5rem]">HP</th>
							<th className="num w-[5.5rem]">DEF</th>
							<th className="num w-[5rem]">Max Lv</th>
							<th>Abilities</th>
							<th className="w-[7.5rem]">Status</th>
							<th className="w-[16rem]">Notes</th>
							<th className="w-8">
								<span className="sr-only">Remove</span>
							</th>
						</tr>
					</thead>
					{GEAR_RARITIES.map((rarity) => (
						<Band key={rarity} slot={slot} rarity={rarity} items={mine.filter((i) => i.rarity === rarity)} onSaving={onSaving} onSaved={onSaved} />
					))}
				</table>
			</div>

			<p className="max-w-2xl text-sm" style={subtle}>
				Click any cell and type; it saves when you leave it. Enter moves down a row, Escape puts the old value back. Stats are the
				item's level 1 numbers as the designer proposes them. Abilities unlock at an item level from 1 to {GEAR_ITEM_MAX_LEVEL}.
			</p>
		</div>
	);
}

interface SaveHooks {
	onSaving: () => void;
	onSaved: () => void;
}

/** One rarity's rows plus the paste-names box that adds to the bottom of the band. */
function Band({ slot, rarity, items, onSaving, onSaved }: { slot: GearSlotId; rarity: GearRarity; items: GearItem[] } & SaveHooks) {
	const [adding, setAdding] = useState(false);
	const [text, setText] = useState("");
	const [busy, setBusy] = useState(false);

	const names = text
		.split(/\r?\n/)
		.map((l) => l.replace(/^\s*(?:[-*•]|\d+[.)])\s*/, "").trim())
		.filter(Boolean);

	async function add() {
		if (names.length === 0) return;
		setBusy(true);
		onSaving();
		try {
			const created = await createGearItems({ slot, rarity, names });
			queryClient.setQueryData<GearItem[]>(["gear-items"], (old) => [...(old ?? []), ...created]);
			toast.success(created.length === 1 ? "Added 1 item" : `Added ${created.length} items`);
			setText("");
			setAdding(false);
			onSaved();
		} catch (err) {
			toast.error(errorMessage(err));
		} finally {
			setBusy(false);
		}
	}

	return (
		<tbody>
			<tr className="band" style={{ borderLeftColor: `var(--color-${rarity})` }}>
				<td colSpan={9}>
					<div className="flex flex-wrap items-center justify-between gap-2">
						<div className="flex items-baseline gap-3">
							<RarityText rarity={rarity}>
								<span className="font-display text-base font-semibold">{RARITY_LABEL[rarity]}</span>
							</RarityText>
							<span className="num text-xs" style={muted}>
								{items.length === 0 ? "no items yet" : items.length === 1 ? "1 item" : `${items.length} items`}
							</span>
						</div>
						{!adding && (
							<button type="button" className="text-xs" style={{ color: "var(--color-gold-400)" }} onClick={() => setAdding(true)}>
								+ Add {RARITY_LABEL[rarity].toLowerCase()} {SLOT_LABEL[slot].toLowerCase()}
							</button>
						)}
					</div>
					{adding && (
						<form
							className="mt-2 grid gap-2 sm:grid-cols-[1fr_auto]"
							onSubmit={(e) => {
								e.preventDefault();
								void add();
							}}
						>
							<textarea
								className="field"
								rows={3}
								autoFocus
								placeholder={"One name per line. A pasted list with dashes is fine:\n- Iron Cap\n- Hunter's Hood"}
								value={text}
								onChange={(e) => setText(e.target.value)}
							/>
							<div className="flex items-start gap-2 sm:flex-col">
								<button type="submit" className="btn btn-primary" disabled={busy || names.length === 0}>
									{busy ? "Adding…" : names.length <= 1 ? "Add item" : `Add ${names.length} items`}
								</button>
								<button
									type="button"
									className="btn"
									onClick={() => {
										setAdding(false);
										setText("");
									}}
								>
									Cancel
								</button>
							</div>
						</form>
					)}
				</td>
			</tr>
			{items.map((it) => (
				<ItemRow key={it.id} item={it} onSaving={onSaving} onSaved={onSaved} />
			))}
		</tbody>
	);
}

function ItemRow({ item, onSaving, onSaved }: { item: GearItem } & SaveHooks) {
	const [open, setOpen] = useState(false);

	async function patch(p: Partial<GearItem>) {
		onSaving();
		try {
			const saved = await updateGearItem(item.id, p);
			queryClient.setQueryData<GearItem[]>(["gear-items"], (old) => (old ?? []).map((i) => (i.id === saved.id ? saved : i)));
			onSaved();
			return true;
		} catch (err) {
			toast.error(errorMessage(err));
			return false;
		}
	}

	async function remove() {
		if (!window.confirm(`Remove "${item.name}" from the catalog? This cannot be undone.`)) return;
		try {
			await deleteGearItem(item.id);
			queryClient.setQueryData<GearItem[]>(["gear-items"], (old) => (old ?? []).filter((i) => i.id !== item.id));
			toast.success(`Removed ${item.name}`);
		} catch (err) {
			toast.error(errorMessage(err));
		}
	}

	const sorted = [...item.abilities].sort((a, b) => a.level - b.level);

	return (
		<>
			<tr data-item className="item">
				<td>
					<TextCell col="name" value={item.name} onCommit={(v) => patch({ name: v })} required />
				</td>
				<td className="num">
					<NumberCell col="atk" value={item.atk} onCommit={(v) => patch({ atk: v })} />
				</td>
				<td className="num">
					<NumberCell col="hp" value={item.hp} onCommit={(v) => patch({ hp: v })} />
				</td>
				<td className="num">
					<NumberCell col="def" value={item.def} onCommit={(v) => patch({ def: v })} />
				</td>
				<td className="num">
					<NumberCell col="max_level" value={item.max_level} min={1} max={GEAR_ITEM_MAX_LEVEL} integer onCommit={(v) => patch({ max_level: v })} />
				</td>
				<td>
					<button
						type="button"
						className="flex min-h-7 w-full flex-wrap items-center gap-1.5 rounded px-1 py-0.5 text-left text-sm hover:bg-[var(--color-ink-800)]"
						aria-expanded={open}
						onClick={() => setOpen((o) => !o)}
					>
						{sorted.length === 0 && (
							<span className="text-xs" style={muted}>
								No abilities. Click to add.
							</span>
						)}
						{sorted.map((a) => (
							<span key={a.level} className="inline-flex items-baseline gap-1 rounded px-1.5 py-0.5 text-xs" style={{ background: "var(--color-ink-800)" }}>
								<span className="num" style={muted}>
									Lv {a.level}
								</span>
								<span>{a.text || a.effect_type || "…"}</span>
							</span>
						))}
					</button>
				</td>
				<td>
					<div className="flex items-center gap-1.5">
						<StatusDot status={item.status} label={false} />
						<select className="cell" value={item.status} onChange={(e) => void patch({ status: e.target.value as AnnotationStatus })} aria-label="Status">
							{ANNOTATION_STATUSES.map((s) => (
								<option key={s} value={s}>
									{STATUS_LABEL[s]}
								</option>
							))}
						</select>
					</div>
				</td>
				<td>
					<TextCell col="notes" value={item.notes} onCommit={(v) => patch({ notes: v })} placeholder="Where it drops, what it's for…" />
				</td>
				<td className="text-center">
					<button type="button" className="rounded px-1.5 text-sm leading-6 hover:text-[var(--color-bad)]" style={muted} onClick={() => void remove()} aria-label={`Remove ${item.name}`}>
						×
					</button>
				</td>
			</tr>
			{open && (
				<tr className="abilities">
					<td colSpan={9}>
						<AbilityEditor item={item} onChange={(abilities) => patch({ abilities })} />
					</td>
				</tr>
			)}
		</>
	);
}

/** Inline editor for one item's level-gated abilities. Each change saves the whole list. */
function AbilityEditor({ item, onChange }: { item: GearItem; onChange: (abilities: GearAbility[]) => Promise<boolean> }) {
	const sorted = [...item.abilities].sort((a, b) => a.level - b.level);
	const listId = `effects-${item.id}`;

	const set = (index: number, p: Partial<GearAbility>) => onChange(sorted.map((a, i) => (i === index ? { ...a, ...p } : a)));
	const removeAt = (index: number) => onChange(sorted.filter((_, i) => i !== index));
	const addOne = () => {
		const used = new Set(sorted.map((a) => a.level));
		let level = 5;
		while (used.has(level) && level < item.max_level) level += 5;
		if (used.has(level)) level = [...Array(item.max_level).keys()].map((n) => n + 1).find((n) => !used.has(n)) ?? item.max_level;
		return onChange([...sorted, { level, text: "", effect_type: "", value: null }]);
	};

	return (
		<div className="space-y-2 py-1 pl-4">
			<div className="grid grid-cols-[4.5rem_1fr_11rem_6rem_2rem] items-center gap-x-3 text-xs" style={subtle}>
				<span>Unlocks at</span>
				<span>What it does</span>
				<span>Effect type (for engineering)</span>
				<span className="text-right">Value</span>
				<span />
			</div>
			{sorted.length === 0 && (
				<p className="text-sm" style={muted}>
					{item.name} has no abilities yet.
				</p>
			)}
			{sorted.map((a, i) => (
				<div key={`${a.level}-${i}`} className="grid grid-cols-[4.5rem_1fr_11rem_6rem_2rem] items-center gap-x-3">
					<div className="flex items-baseline gap-1">
						<span className="text-xs" style={muted}>
							Lv
						</span>
						<NumberCell col={`ab-level-${i}`} value={a.level} min={1} max={item.max_level} integer onCommit={(v) => set(i, { level: v })} />
					</div>
					<TextCell col={`ab-text-${i}`} value={a.text} placeholder="+10% fire damage" onCommit={(v) => set(i, { text: v })} />
					<TextCell col={`ab-type-${i}`} value={a.effect_type} placeholder="element_damage" list={listId} mono onCommit={(v) => set(i, { effect_type: v })} />
					<NumberCell col={`ab-value-${i}`} value={a.value} placeholder="0.10" allowEmpty onCommit={(v) => set(i, { value: v })} />
					<button type="button" className="rounded px-1.5 text-sm leading-6 hover:text-[var(--color-bad)]" style={muted} onClick={() => void removeAt(i)} aria-label="Remove ability">
						×
					</button>
				</div>
			))}
			<datalist id={listId}>
				{EFFECT_TYPE_SUGGESTIONS.map((t) => (
					<option key={t} value={t} />
				))}
			</datalist>
			<button type="button" className="text-xs" style={{ color: "var(--color-gold-400)" }} onClick={() => void addOne()} disabled={sorted.length >= item.max_level}>
				+ Add ability
			</button>
		</div>
	);
}

// ── Cells ───────────────────────────────────────────────────────────────────

/** Enter commits and moves to the same column one row down, like a sheet. */
function moveDown(input: HTMLInputElement) {
	const row = input.closest("tr[data-item]");
	let next = row?.nextElementSibling;
	while (next && !next.hasAttribute("data-item")) next = next.nextElementSibling;
	const target = next?.querySelector<HTMLInputElement>(`input[data-col="${input.dataset["col"]}"]`);
	target?.focus();
	target?.select();
}

interface TextCellProps {
	col: string;
	value: string;
	onCommit: (v: string) => Promise<boolean>;
	placeholder?: string;
	required?: boolean;
	list?: string;
	mono?: boolean;
}

function TextCell({ col, value, onCommit, placeholder, required, list, mono }: TextCellProps) {
	const [draft, setDraft] = useState(value);
	const [base, setBase] = useState(value);
	if (base !== value) {
		// The row was replaced by a save or a refetch: adopt the new value.
		setBase(value);
		setDraft(value);
	}

	async function commit() {
		const v = draft.trim();
		if (v === value) {
			setDraft(value);
			return;
		}
		if (required && !v) {
			setDraft(value);
			return;
		}
		if (!(await onCommit(v))) setDraft(value);
	}

	return (
		<input
			data-col={col}
			className={`cell${mono ? " font-mono text-xs" : ""}`}
			value={draft}
			placeholder={placeholder}
			list={list}
			onChange={(e) => setDraft(e.target.value)}
			onBlur={() => void commit()}
			onKeyDown={(e) => {
				if (e.key === "Enter") {
					e.preventDefault();
					moveDown(e.currentTarget);
					if (document.activeElement === e.currentTarget) e.currentTarget.blur();
				} else if (e.key === "Escape") {
					setDraft(value);
					e.currentTarget.blur();
				}
			}}
		/>
	);
}

interface NumberCellProps {
	col: string;
	value: number | null;
	onCommit: (v: number) => Promise<boolean>;
	min?: number;
	max?: number;
	integer?: boolean;
	placeholder?: string;
	/** Commit `null` when cleared (ability value). */
	allowEmpty?: boolean;
}

function NumberCell({ col, value, onCommit, min = 0, max, integer, placeholder, allowEmpty }: NumberCellProps) {
	const shown = value === null ? "" : String(value);
	const [draft, setDraft] = useState(shown);
	const [base, setBase] = useState(shown);
	if (base !== shown) {
		setBase(shown);
		setDraft(shown);
	}

	async function commit() {
		const raw = draft.trim();
		if (raw === shown) return;
		if (raw === "") {
			if (allowEmpty) {
				if (!(await (onCommit as unknown as (v: null) => Promise<boolean>)(null))) setDraft(shown);
			} else setDraft(shown);
			return;
		}
		let n = Number(raw.replace(/,/g, ""));
		if (!Number.isFinite(n)) {
			toast.error("That is not a number");
			setDraft(shown);
			return;
		}
		if (integer) n = Math.round(n);
		if (n < min || (max !== undefined && n > max)) {
			toast.error(max !== undefined ? `Must be between ${min} and ${max}` : `Must be at least ${min}`);
			setDraft(shown);
			return;
		}
		if (!(await onCommit(n))) setDraft(shown);
	}

	return (
		<input
			data-col={col}
			className="cell num text-right"
			inputMode="decimal"
			value={draft}
			placeholder={placeholder ?? "0"}
			onChange={(e) => setDraft(e.target.value)}
			onFocus={(e) => e.currentTarget.select()}
			onBlur={() => void commit()}
			onKeyDown={(e) => {
				if (e.key === "Enter") {
					e.preventDefault();
					moveDown(e.currentTarget);
					if (document.activeElement === e.currentTarget) e.currentTarget.blur();
				} else if (e.key === "Escape") {
					setDraft(shown);
					e.currentTarget.blur();
				}
			}}
		/>
	);
}
